import { onAuthStateChanged } from 'firebase/auth';
import React, { useEffect, useState } from 'react';

import { postTrelloToken } from '../shared/api/integration';
import { auth } from '../shared/firebase/firebase';

type Status = 'connecting' | 'success' | 'error';

const parseHashToken = (): string | null => {
  const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
  const params = new URLSearchParams(hash);
  return params.get('token');
};

const ConnectTrelloPage: React.FC = () => {
  const [status, setStatus] = useState<Status>('connecting');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = parseHashToken();
    if (!token) {
      setError('No Trello token was returned. Please try connecting again.');
      setStatus('error');
      return;
    }

    const unsub = onAuthStateChanged(auth, (user) => {
      unsub();
      if (!user) {
        setError('Not authenticated. Please close this window and try again.');
        setStatus('error');
        return;
      }

      postTrelloToken(token)
        .then(() => {
          if (window.opener) window.opener.postMessage({ type: 'trello-connected' }, '*');
          setStatus('success');
          window.close();
        })
        .catch(() => {
          setError('Failed to save your Trello connection. Please try again.');
          setStatus('error');
        });
    });
  }, []);

  return (
    <div className="min-h-screen bg-base-200 flex items-center justify-center p-4">
      <div className="card bg-base-100 shadow-xl w-full max-w-sm">
        <div className="card-body gap-4 items-center text-center">
          {status === 'connecting' && (
            <>
              <h2 className="card-title font-display">Connecting to Trello…</h2>
              <span className="loading loading-spinner loading-md" />
            </>
          )}

          {status === 'success' && (
            <>
              <h2 className="card-title font-display">Trello Connected</h2>
              <p className="text-sm opacity-70">You can close this window.</p>
              <button className="btn btn-primary btn-sm" onClick={() => window.close()}>
                Close
              </button>
            </>
          )}

          {status === 'error' && (
            <>
              <h2 className="card-title font-display text-error">Trello Connection Error</h2>
              <div className="alert alert-error">
                <span className="text-sm">{error}</span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => window.close()}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConnectTrelloPage;
